import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Euro, ShoppingBag, Users, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { REVENUE_14D, CUSTOMERS, loadOrders } from "../../data/adminData";
import { PRODUCTS, demoStats, fmt } from "../../data/store";
import { StatusBadge } from "./Orders";

export default function Dashboard() {
  const orders = useMemo(() => loadOrders(), []);
  const revenue = REVENUE_14D.reduce((s, d) => s + d.revenue, 0);
  const top = [...PRODUCTS].sort((a, b) => demoStats(b).soldWeek - demoStats(a).soldWeek).slice(0, 5)
    .map((p) => ({ name: p.name.slice(0, 22) + "…", sold: demoStats(p).soldWeek }));

  const kpis = [
    { icon: Euro, label: "Ieņēmumi (14 d.)", value: fmt(revenue) },
    { icon: ShoppingBag, label: "Pasūtījumi", value: orders.length },
    { icon: TrendingUp, label: "Vidējais grozs", value: fmt(orders.reduce((s, o) => s + o.total, 0) / orders.length) },
    { icon: Users, label: "Klienti", value: CUSTOMERS.length },
  ];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <h1 className="text-2xl font-extrabold uppercase tracking-tight">Pārskats</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.label} className="bg-white border border-[#E2E2E2] p-5" data-testid={`kpi-${k.label.split(" ")[0].toLowerCase()}`}>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#6B6B6B]"><k.icon size={14} /> {k.label}</div>
            <div className="text-2xl font-extrabold mt-2">{k.value}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2E2E2] p-5">
          <h2 className="text-sm font-bold uppercase tracking-widest mb-4">Ieņēmumi pa dienām</h2>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={REVENUE_14D}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E2E2" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => [`${v} €`, "Ieņēmumi"]} />
              <Line type="monotone" dataKey="revenue" stroke="#D0121A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E2E2E2] p-5">
          <h2 className="text-sm font-bold uppercase tracking-widest mb-4">Top produkti (pārdots nedēļā)</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={top} layout="vertical" margin={{ left: 40 }}>
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v) => [`${v} gab.`, "Pārdots"]} />
              <Bar dataKey="sold" fill="#111111" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-white border border-[#E2E2E2] p-5">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-sm font-bold uppercase tracking-widest">Jaunākie pasūtījumi</h2>
          <Link to="/admin/pasutijumi" className="text-xs font-bold text-[#D0121A] hover:underline">Visi pasūtījumi →</Link>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-[#6B6B6B] border-b border-[#E2E2E2]">
            <th className="py-2">Nr.</th><th>Klients</th><th className="hidden sm:table-cell">Datums</th><th>Statuss</th><th className="text-right">Summa</th>
          </tr></thead>
          <tbody>
            {orders.slice(0, 5).map((o) => (
              <tr key={o.nr} className="border-b border-[#F5F5F5]">
                <td className="py-2.5 font-bold">{o.nr}</td>
                <td>{o.customer || "—"}</td>
                <td className="hidden sm:table-cell text-[#6B6B6B]">{new Date(o.date).toLocaleDateString("lv-LV")}</td>
                <td><StatusBadge status={o.status} /></td>
                <td className="text-right font-bold">{fmt(o.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
